/**
 * /settings → Audit-log (admin-only).
 *
 * Toont alle recovery-acties van deze workspace: prullenbak-restore,
 * prullenbak-purge, snapshot-create, snapshot-restore, snapshot-delete.
 * Filterbaar per action-type.
 *
 * Read-only: de UI is een audit-trail, geen control-plane. Acties
 * zelf zitten in hun eigen tabbladen (Snapshots, Prullenbak).
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type RecoveryAuditAction, type RecoveryAuditEntry } from "../api/client";
import { useSession } from "../state/session";

interface Props {
  workspaceId: string;
}

const ACTION_TONE: Record<RecoveryAuditAction, { icon: string; tone: string }> = {
  trash_restore: { icon: "↩", tone: "text-(--ld-positive-ink) bg-(--ld-positive-bg)" },
  trash_purge: { icon: "🗑", tone: "text-(--ld-negative) bg-(--ld-negative-bg)" },
  snapshot_create: { icon: "📸", tone: "text-(--ld-accent) bg-(--ld-active)" },
  snapshot_restore: { icon: "↩", tone: "text-(--ld-notice-ink) bg-(--ld-notice-bg)" },
  snapshot_delete: { icon: "🗑", tone: "text-(--ld-negative) bg-(--ld-negative-bg)" },
};

const ACTION_LABEL_KEY: Record<RecoveryAuditAction, string> = {
  trash_restore: "settings.audit.actionTrashRestore",
  trash_purge: "settings.audit.actionTrashPurge",
  snapshot_create: "settings.audit.actionSnapshotCreate",
  snapshot_restore: "settings.audit.actionSnapshotRestore",
  snapshot_delete: "settings.audit.actionSnapshotDelete",
};

export function AuditSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const branchId = useSession((s) => s.branchId);
  const filterOptions: Array<{ value: "" | RecoveryAuditAction; label: string }> = [
    { value: "", label: t("settings.audit.filterAll") },
    { value: "trash_restore", label: t("settings.audit.actionTrashRestore") },
    { value: "trash_purge", label: t("settings.audit.actionTrashPurgeShort") },
    { value: "snapshot_create", label: t("settings.audit.actionSnapshotCreate") },
    { value: "snapshot_restore", label: t("settings.audit.actionSnapshotRestore") },
    { value: "snapshot_delete", label: t("settings.audit.actionSnapshotDelete") },
  ];
  const [entries, setEntries] = useState<RecoveryAuditEntry[]>([]);
  const [actionFilter, setActionFilter] = useState<RecoveryAuditAction | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!branchId) return;
    setError(null);
    setLoading(true);
    try {
      const opts: Parameters<typeof api.listRecoveryAudit>[2] = { limit: 200 };
      if (actionFilter) opts.action = actionFilter;
      const res = await api.listRecoveryAudit(workspaceId, branchId, opts);
      setEntries(res.entries);
    } catch (err) {
      setError(explain(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, branchId, actionFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="flex flex-col gap-4">
      <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-text) text-sm">
        <p className="mb-1">
          <strong>{t("settings.audit.introBoldTitle")}</strong>
          {t("settings.audit.introAfter")}
        </p>
        <p className="text-(--ld-muted) text-xs">
          {t("settings.audit.introDetailBefore")}
          <strong>{t("settings.audit.introDetailSnapshots")}</strong>
          {t("settings.audit.introDetailAnd")}
          <strong>{t("settings.audit.introDetailTrash")}</strong>
          {t("settings.audit.introDetailAfter")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-(--ld-text) text-sm">
          <span>{t("settings.audit.filterLabel")}</span>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter((e.target.value as RecoveryAuditAction | "") ?? "")}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
            disabled={loading}
          >
            {filterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
        >
          {t("settings.common.refresh")}
        </button>
      </div>

      {error && (
        <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
          {error}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="rounded border border-dashed border-(--ld-control) bg-(--ld-surface) px-3 py-6 text-center text-(--ld-muted) text-sm italic">
          {loading ? t("settings.common.loading") : t("settings.audit.emptyNoFilter")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AuditRow({ entry }: { entry: RecoveryAuditEntry }) {
  const { t } = useTranslation();
  const tone = ACTION_TONE[entry.action];
  return (
    <li className="rounded border border-(--ld-border) bg-(--ld-surface) px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`rounded px-2 py-0.5 font-medium text-xs ${tone.tone}`}>
            {t(ACTION_LABEL_KEY[entry.action])}
          </span>
          <span className="text-(--ld-muted) text-xs">{entry.performed_by}</span>
        </div>
        <span className="text-(--ld-muted) text-xs tabular-nums">
          {formatTimestamp(entry.performed_at)}
        </span>
      </div>
      <Details entry={entry} />
    </li>
  );
}

function Details({ entry }: { entry: RecoveryAuditEntry }) {
  const { t } = useTranslation();
  const d = entry.details;
  switch (entry.action) {
    case "trash_restore":
      return (
        <p className="mt-1 text-(--ld-muted) text-xs">
          {t("settings.audit.detailTrashRestore", {
            concepts: String(d.concepts_restored ?? 0),
            relations: String(d.relations_restored ?? 0),
          })}
          {Array.isArray(d.relations_skipped_dangling) &&
            d.relations_skipped_dangling.length > 0 && (
              <span className="ml-2 text-(--ld-notice-ink)">
                {t("settings.audit.detailTrashRestoreSkipped", {
                  count: d.relations_skipped_dangling.length,
                })}
              </span>
            )}
        </p>
      );
    case "trash_purge": {
      const mode = String(d.mode ?? "?");
      const cutoff = d.older_than
        ? t("settings.audit.detailTrashPurgeOlderThan", { date: String(d.older_than) })
        : "";
      return (
        <p className="mt-1 text-(--ld-muted) text-xs">
          {t("settings.audit.detailTrashPurgeBefore")}
          <code className="rounded bg-(--ld-active) px-1">{mode}</code>
          {cutoff}
          {t("settings.audit.detailTrashPurgeAfter", {
            concepts: String(d.concepts_purged ?? 0),
            relations: String(d.relations_purged ?? 0),
          })}
        </p>
      );
    }
    case "snapshot_create": {
      const counts = d.counts as
        | { concepts?: number; relations?: number; diagrams?: number }
        | undefined;
      return (
        <p className="mt-1 text-(--ld-muted) text-xs">
          <strong>"{String(d.label ?? "")}"</strong>
          {d.message ? ` — ${String(d.message)}` : ""}
          {counts && (
            <>
              {" "}
              ({counts.concepts ?? 0}c · {counts.relations ?? 0}r · {counts.diagrams ?? 0}d,{" "}
              {formatBytes(d.byte_size)})
            </>
          )}
        </p>
      );
    }
    case "snapshot_restore": {
      const summary = d.summary as Record<string, number> | undefined;
      return (
        <div className="mt-1 text-(--ld-muted) text-xs">
          {t("settings.audit.detailSnapshotRestoreFrom")}
          <strong>"{String(d.label ?? "")}"</strong>
          {typeof d.safety_snapshot_label === "string" && (
            <>
              {t("settings.audit.detailSnapshotRestoreSafety")}
              <em>"{d.safety_snapshot_label}"</em>
            </>
          )}
          {summary && (
            <div className="mt-0.5 text-(--ld-muted) tabular-nums">
              {/*
                Toon tombstone én delete apart als ze allebei > 0 zijn,
                anders sommeer ze tot één getal. Veld `_deleted` is
                toegevoegd 2026-05-24 (zie snapshots.ts ApplySummary)
                zodat audit-log eerlijk onderscheid maakt tussen
                soft-tombstone op feature-branch en hard-delete op main.
                Oude rijen vóór die datum hebben alleen
                `_tombstoned` — voor die wordt het delete-deel 0.
              */}
              concepts +{summary.concepts_inserted ?? 0} ~{summary.concepts_updated ?? 0} -
              {(summary.concepts_tombstoned ?? 0) + ((summary.concepts_deleted as number) ?? 0)}
              {(summary.concepts_deleted as number) > 0 ? " (hard-deleted)" : ""} · relations +
              {summary.relations_inserted ?? 0} ~{summary.relations_updated ?? 0} -
              {(summary.relations_tombstoned ?? 0) + ((summary.relations_deleted as number) ?? 0)}
              {(summary.relations_deleted as number) > 0 ? " (hard-deleted)" : ""} · diagrams ~
              {summary.diagrams_replaced ?? 0} -{summary.diagrams_removed ?? 0}
            </div>
          )}
        </div>
      );
    }
    case "snapshot_delete": {
      const counts = d.counts as
        | { concepts?: number; relations?: number; diagrams?: number }
        | undefined;
      return (
        <p className="mt-1 text-(--ld-muted) text-xs">
          <strong>"{String(d.label ?? "")}"</strong>
          {t("settings.audit.detailSnapshotDeleteSuffix")}
          {counts && (
            <>
              {" "}
              ({counts.concepts ?? 0}c · {counts.relations ?? 0}r · {counts.diagrams ?? 0}d,{" "}
              {formatBytes(d.byte_size)})
            </>
          )}
          .
        </p>
      );
    }
    default:
      return null;
  }
}

function formatBytes(bytes: unknown): string {
  if (typeof bytes !== "number" || !Number.isFinite(bytes)) return "?";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTimestamp(ts: string): string {
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return ts;
  return new Date(ms).toLocaleString();
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
